import { type FC, useMemo, useState } from 'react'
import { Badge, Modal as BootstrapModal, Spinner, Tab } from 'react-bootstrap'
import classNames from 'classnames'
import { getIntegrationDisplayName, type WorkflowSupportedIntegration, isWorkflowSupportedIntegration } from '../../../../../../common/integrations'
import { Button } from '../../../../../../components/Button/Button'
import { IntegrationIcon } from '../../../../../../components/IntegrationIcon'
import { Modal } from '../../../../../../components/Modal/Modal'
import { Tabs } from '../../../../../../components/Tabs/Tabs'
import { type User } from '../../../../../../models/User'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectRoomSession } from '../../../../../../store/meetingSummary/roomSession'
import { toIntegrationWorkflowTarget } from './integrationWorkflow'
import { getIntegrationWorkflowAdapter } from './integrationWorkflowAdapters'
import { NewWorkflowTab } from './NewWorkflowTab'
import { RunningWorkflowsTab } from './RunningWorkflowsTab'
import { SuggestionsTab } from './SuggestionsTab'
import { useIntegrationWorkflows } from './useIntegrationWorkflows'

const INTEGRATION_WORKFLOWS_TABS = ['suggestions', 'newWorkflow', 'runningWorkflows'] as const

type IntegrationWorkflowsTab = (typeof INTEGRATION_WORKFLOWS_TABS)[number]

const isIntegrationWorkflowsTab = (key: string | null): key is IntegrationWorkflowsTab => INTEGRATION_WORKFLOWS_TABS.some((tab) => tab === key)

const UNLINKED_MESSAGE = 'This contact is not linked to your CRM yet, so there are no workflows to show.'
const ERROR_MESSAGE = 'We could not load workflows from your CRM.'

interface IntegrationWorkflowsModalProps {
  user: User
  serviceId: string
  show: boolean
  onClose: () => void
}

interface TabTitleProps {
  label: string
  count: number
  icon?: string
}

const TabTitle: FC<TabTitleProps> = ({ label, count, icon }) => (
  <span className='d-flex align-items-center gap-2 text-uppercase'>
    {icon ? <i className={classNames('bi', icon)} /> : null}
    {label}
    {count ? (
      <Badge bg='' pill className='zocks-tag-blue'>
        {count}
      </Badge>
    ) : null}
  </span>
)

const IntegrationWorkflowsModalContent: FC<IntegrationWorkflowsModalProps & { serviceId: WorkflowSupportedIntegration; roomId: string; sessionId: string }> = ({
  user,
  serviceId,
  roomId,
  sessionId,
  show,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<IntegrationWorkflowsTab>('suggestions')
  const target = useMemo(() => toIntegrationWorkflowTarget(serviceId, user), [serviceId, user])
  const { status, retry, running, completed, templates, suggestions, counts } = useIntegrationWorkflows({ target, roomId, sessionId })

  const { capabilities } = getIntegrationWorkflowAdapter(serviceId)

  const handleWorkflowStarted = (templateId: string) => {
    suggestions.markTemplateHandled(templateId)
    setActiveTab('runningWorkflows')
  }

  const body =
    status === 'loading' ? (
      <div className='d-flex align-items-center justify-content-center p-4'>
        <Spinner />
      </div>
    ) : target && status === 'ready' ? (
      <Tabs activeKey={activeTab} onSelect={(key) => (isIntegrationWorkflowsTab(key) ? setActiveTab(key) : null)}>
        <Tab eventKey='suggestions' title={<TabTitle label='Suggestions' count={counts.suggestions} icon='bi-stars' />}>
          <SuggestionsTab target={target} suggestions={suggestions} capabilities={capabilities} onStarted={handleWorkflowStarted} />
        </Tab>
        <Tab eventKey='newWorkflow' title={<TabTitle label='New Workflow' count={0} />}>
          <NewWorkflowTab target={target} templates={templates} onStarted={handleWorkflowStarted} />
        </Tab>
        <Tab
          eventKey='runningWorkflows'
          title={<TabTitle label='Running Workflows' count={counts.running} />}
          disabled={running.length + completed.length === 0}
        >
          <RunningWorkflowsTab
            target={target}
            running={running}
            completed={completed}
            capabilities={capabilities}
            onStepCompleted={suggestions.markWorkflowHandled}
          />
        </Tab>
      </Tabs>
    ) : (
      <div className='d-flex flex-column align-items-center justify-content-center gap-2 p-4 paragraph-small zocks-text-gray-600'>
        <div>{status === 'error' ? ERROR_MESSAGE : UNLINKED_MESSAGE}</div>
        {status === 'error' ? (
          <Button variant='secondary' className='button-small' onClick={retry}>
            Try again
          </Button>
        ) : null}
      </div>
    )

  return (
    <Modal
      size='xl'
      centered={true}
      scrollable={true}
      show={show}
      onHide={onClose}
      bodyClassName='pb-4'
      header={
        <BootstrapModal.Title className='d-flex align-items-center'>
          <IntegrationIcon serviceId={serviceId} className='me-2' width='2rem' height='2rem' />
          Manage Workflows In {getIntegrationDisplayName(serviceId)}
        </BootstrapModal.Title>
      }
      body={body}
    />
  )
}

export const IntegrationWorkflowsModal: FC<IntegrationWorkflowsModalProps> = (props) => {
  const roomSession = useAppSelector(selectRoomSession)

  if (!roomSession || !isWorkflowSupportedIntegration(props.serviceId)) {
    return null
  }

  return <IntegrationWorkflowsModalContent {...props} serviceId={props.serviceId} roomId={roomSession.roomId} sessionId={roomSession.id} />
}
